<template>
  <div class="position-relative d-flex flex-column"
       id="q-app">
    <b-overlay class="h-100 w-100 position-absolute"
               :show="isPageLoading">
      <template #overlay>
        <app-spinner :size="32"/>
      </template>
    </b-overlay>
    <!-- A suspended, expired or canceled account's page stands alone -->
    <header-notification class="flex-grow-0"
                         v-if="isLoggedIn && !isWidget && !suspended" />

    <router-view class="flex-grow-1 overflow-hidden"
                 v-if="cookieValidated" />
    <portal-target name="app"
                   multiple />
    <action-notification id="system"
                         v-if="!isWidget" />
    <action-notification id="sms"
                         v-if="!isWidget" />
    <action-notification id="call"
                         v-if="!isWidget" />
    <action-notification id="voicemail"
                         v-if="!isWidget" />
    <action-notification id="mention"
                         v-if="!isWidget" />
    <action-notification id="reminder"
                         v-if="!isWidget" />
    <action-notification id="incomingCall"
                         v-if="isWidget ? isSoftphoneWidget : true"
                         position="b-toaster-top-center" />
    <intercom v-if="isIntercomEnabled && !isWidget" />
  </div>
</template>
<script>
import { Platform } from 'quasar'
import { SHARED_AUTH_COOKIE } from 'src/constants/shared-auth-cookie'
import ActionNotification from 'components/action-notification'
import AppSpinner from 'components/layout/app-spinner.vue'
import { applySeason, listenForSeasonDemo } from 'src/plugins/helpers/season-demo'
import { followTheme, watchColorTheme } from 'src/plugins/helpers/color-theme'
import talk2Api from 'src/plugins/api/api'
import HeaderNotification from 'components/header-notification'
import Intercom from 'components/intercom'
import * as storage from 'src/plugins/helpers/storage'
import { getCookie } from 'src/plugins/helpers/functions'
import { requestSanitizer, responseSanitizer } from 'src/plugins/helpers/logrocket-sanitizers'
import { accessMixin } from 'src/plugins/mixins'
import { isAllowedRedirectSource } from 'src/store/auth/auth.actions'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'App',

  mixins: [
    accessMixin
  ],

  components: {
    AppSpinner,
    HeaderNotification,
    Intercom,
    ActionNotification
  },

  data () {
    return {
      cookieValidated: false,
      sharedCookie: null,
      isPageLoading: false,
      logrocketAppId: process.env.APP_ENV === 'production' ? process.env.LOGROCKET_APP_ID : null,
      logrocketInitialized: false,
      isWhitelabel: false
    }
  },

  computed: {
    ...mapState('auth', ['profile', 'authenticated', 'loading']),
    ...mapState(['statics', 'staticsLoaded', 'isWhiteLabel', 'isWidget', 'isSalesforceWidget', 'isHubSpotWidget', 'widgetPlatform', 'justLoggedIn', 'suspended']),
    ...mapGetters(['isSoftphoneWidget']),

    isLoggedIn () {
      return this.authenticated && this.profile && this.profile.enabled
    },

    isIntercomEnabled () {
      return this.isLoggedIn && this.staticsLoaded && !this.isWhitelabel
    }
  },

  created () {
    this.stopSeasonDemo = listenForSeasonDemo(this.refetchStatics)
    this.stopColorTheme = watchColorTheme()
    // Quasar's components draw themselves dark while the page is
    this.stopQuasarDark = followTheme((dark) => this.$q.dark.set(dark))

    // Handle api-core stopping impersonation — clear Talk2 state and redirect back.
    // Must run before the shared-cookie check because stopImpersonation clears the auth cookie first.
    const urlParams = new URLSearchParams(window.location.search)
    const fromCore = urlParams.get('from_core')
    const coreLogout = urlParams.get('logout')
    const coreSource = urlParams.get('source')
    const rawReturnPath = (urlParams.get('return') || '/login').trim()
    const returnPath = rawReturnPath.startsWith('/') && !rawReturnPath.startsWith('//') ? rawReturnPath : '/login'

    if (fromCore && coreLogout) {
      this.clearUser()
      const target = coreSource && isAllowedRedirectSource(decodeURIComponent(coreSource))
        ? decodeURIComponent(coreSource) + returnPath
        : '/'
      window.location.href = target
      return
    }

    // The saved api_token is whoever this browser was last authenticated as.
    // When the shared cookie names someone else — impersonation, stopping it,
    // any cross-app identity change — it is stale, but bootstrapSession() still
    // asks /get-auth-user with it and is answered with that previous user.
    // Everything the boot then establishes off that answer is bound to the wrong
    // identity, the Twilio Device included: it registers as the previous user,
    // on the previous company's subaccount, and nothing re-registers it when the
    // cookie check that follows in mounted() corrects the profile in place.
    //
    // Dropping the token here leaves the cookie as the only thing that can
    // answer, which is the one identity that is actually current. Same condition
    // mounted() already re-authenticates on, applied before the request races it
    // rather than after. A missing cookie is the browser-was-closed case and
    // still belongs to bootstrapSession().
    const sharedCookie = getCookie(SHARED_AUTH_COOKIE)

    if (sharedCookie && storage.local.getItem('shared_cookie') !== sharedCookie && this.$route.name !== 'Login') {
      storage.local.removeItem('api_token')
      storage.local.removeItem('company_id')
      storage.local.removeItem('current_user_id')

      return
    }

    this.bootstrapSession()
  },

  mounted () {
    // proceed to cookie validation if account is talk allowed access
    this.getSharedCookie().then(sharedCookie => {
      this.sharedCookie = sharedCookie

      // Only re-auth on shared-cookie change when the cookie is actually present — a missing
      // cookie just means the browser dropped it (e.g. session cookie after close) and
      // bootstrapSession already determined the auth state via /get-auth-user.
      if (this.sharedCookie && storage.local.getItem('shared_cookie') !== this.sharedCookie && this.$route.name !== 'Login') {
        this.validateCookieUser()
      }

      // Sync company selection: if the other app switched companies, re-authenticate
      const selectedCompanyId = parseInt(getCookie('selected_company_id'))
      const currentCompanyId = parseInt(storage.local.getItem('company_id'))
      if (selectedCompanyId && currentCompanyId && selectedCompanyId !== currentCompanyId && this.$route.name !== 'Login') {
        this.validateCookieUser()
      }

      if (!selectedCompanyId && currentCompanyId && this.profile?.has_multiple_access && this.$route.name !== 'Login') {
        this.getAccesses(false)
      }
    })

    this.$VueEvent.listen('make_new_call', (data) => {
      let fixedPhoneNumber = this.$options.filters.fixPhone(data.phone_number)
      this.isPageLoading = true

      if (/unhold:|barge:|whisper:|call:|hs:/.test(fixedPhoneNumber)) {
        window.axios.get('/api/v1/communication/info', {
          params: {
            sid: '',
            phone_number: fixedPhoneNumber
          }
        }).then(res => {
          this.isPageLoading = false

          this.$VueEvent.fire('makeCall', {
            currentNumber: fixedPhoneNumber,
            outboundCampaignId: res.data.campaign_id,
            contactName: res.data?.contact?.name,
            companyName: res.data?.contact?.company_name,
            contactId: res.data?.contact?.id,
            contactTimezone: res.data?.contact?.timezone
          })
        }).catch(() => {
          this.isPageLoading = false
        })

        return
      }

      const requestData = {
        phone_number: fixedPhoneNumber
      }

      if (data.first_name) {
        requestData.first_name = data.first_name
      }

      if (data.last_name) {
        requestData.last_name = data.last_name
      }

      if (data.is_company) {
        requestData.is_company = data.is_company
      }

      window.axios.post('/api/v2/contacts/click-to-call', requestData).then(res => {
        this.isPageLoading = false
        const contact = res.data
        const callData = {
          currentNumber: fixedPhoneNumber,
          contactName: contact.name,
          companyName: contact.company_name,
          contactId: contact.id,
          contactTimezone: contact.timezone
        }

        const route = data.open_contact_page ? '/contacts/' + callData.contactId : {
          name: 'Phone'
        }

        this.$router.push(route)
        this.$VueEvent.fire('callContact', callData)
      }).catch(() => {
        this.isPageLoading = false
      })
    })

    this.$VueEvent.listen('add_contact', (data) => {
      this.isPageLoading = true
      const requestData = {
        phone_number: this.$options.filters.fixPhone(data.phone_number)
      }

      if (data.first_name) {
        requestData.first_name = data.first_name
      }

      if (data.last_name) {
        requestData.last_name = data.last_name
      }

      if (data.is_company === 'true') {
        requestData.is_company = true
      }

      window.axios.post('/api/v2/contacts/click-to-call', requestData).then(res => {
        this.isPageLoading = false
        this.$router.replace('/contacts/' + res.data.id)
          .catch(this.$handleRouteError)
      }).catch(() => {
        this.isPageLoading = false
        this.$router.replace('/')
          .catch(this.$handleRouteError)
      })
    })

    this.$VueEvent.listen('user_logout', async (data) => {
      if (this.authenticated) {
        this.clearUser()
        this.$router.push({ name: 'Login' })
          .catch(this.$handleRouteError)
      }
    })
  },

  beforeDestroy () {
    this.stopSeasonDemo()
    this.stopColorTheme()
    this.stopQuasarDark()
  },

  watch: {
    authenticated () {
      this.setSessionReplay()
      this.addSentryContext()
    },

    statics: {
      immediate: true,
      handler (statics) {
        applySeason(statics)
      }
    }
  },

  methods: {
    // Cold-start auth: ask api-core "am I logged in?" using the Bearer token (from
    // localStorage) and the laravel_session cookie (sent automatically via withCredentials
    // for same-parent-domain requests). This mirrors how the main dashboard decides auth
    // state, so Talk survives browser close the same way the dashboard does.
    async bootstrapSession () {
      try {
        const response = await this.checkSession({})
        const user = response?.data?.user
        const company = user?.company
        const previousCompanyId = parseInt(storage.local.getItem('company_id'))

        if (company) {
          // Defense in depth: if the authenticated company changed since the last boot,
          // drop any persisted Power Dialer session so it cannot resume (and dial its
          // contacts/line/campaign) under the new company. Covers company switches that
          // bypass companyLogin(), e.g. switching company in the dashboard. The persisted
          // session is otherwise kept across reloads, so a plain non-cache reset is not enough.
          if (previousCompanyId && previousCompanyId !== company.id) {
            this.resetVuex(['power-dialer'])
          }

          this.setCurrentCompany(company)
          storage.local.setItem('company_id', company.id)
        }

        this.cookieValidated = true

        if (company && (this.$route.name === 'Login' || previousCompanyId !== company.id)) {
          this.checkAccesses()
        }

        if (this.$route.name === 'Login') {
          this.$router.push(this.$route.query.redirect || '/').catch(this.$handleRouteError)
        }
      } catch (err) {
        this.cookieValidated = true

        // Skip for iframes — widgets authenticate via api_key in the URL, not cookies.
        if (this.$route.name !== 'Login' && !Platform.within.iframe) {
          this.clearUser()
          this.$router.push({ name: 'Login' }).catch(this.$handleRouteError)
        }
      }
    },

    async validateCookieUser () {
      if (!this.sharedCookie) {
        // Cold-boot auth is handled by bootstrapSession — do not destroy auth state here.
        this.cookieValidated = true
        return
      }

      this.getCookieUser().then(response => {
        this.cookieUserValidated(response)
      }).catch(() => {
        this.cookieValidated = true
        this.logout()
      })
    },

    async cookieUserValidated ({ data: { data } }) {
      const {
        usage,
        company
      } = data
      const previousCompanyId = parseInt(storage.local.getItem('company_id'))
      // getCookieUser() has already decided whether this run is a login — it
      // sets the marker only where it established auth that did not exist
      // before. Carry that decision across the reset instead of re-asserting
      // it: mounted() also lands here on a rotated shared cookie and on a
      // dashboard company switch, and those keep whatever status the agent is
      // already on. Read before, restore after, as onLoginSuccess() does, so
      // it does not depend on the root RESET_VUEX mutation being a no-op.
      const isLogin = this.justLoggedIn
      this.resetVuex(['non-cache'])
      if (isLogin) {
        this.setJustLoggedIn(true)
      }
      // Defense in depth: a non-cache reset deliberately preserves the persisted Power
      // Dialer session, so on a company change it must be cleared explicitly — otherwise a
      // session started under the previous company resumes (and dials its contacts/line/
      // campaign) under the new one. Covers switches that bypass companyLogin().
      if (previousCompanyId && previousCompanyId !== company.id) {
        this.resetVuex(['power-dialer'])
      }
      this.setCurrentCompany(company)
      this.setUsage(usage)
      this.cookieValidated = true

      storage.local.setItem('shared_cookie', this.sharedCookie)
      storage.local.setItem('company_id', company.id)

      if (this.$route.name === 'Login' || previousCompanyId !== company.id) {
        this.checkAccesses()
      }

      if (this.$route.name === 'Login') {
        this.$router.push(this.$route.query.redirect || '/').catch(this.$handleRouteError)
      }
    },

    logout () {
      this.logoutUser().then((res) => {
        this.setSessionReplay()
        this.response = res.data
        this.$router.push({ name: 'Login' })
          .catch(this.$handleRouteError)
      }).catch((err) => {
        console.log(err)
      })
    },

    // Identify the authenticated user to the session-replay tool (LogRocket).
    sessionReplayIdentify (profile) {
      if (!profile) {
        return
      }

      window.axios.get('/session-replay-meta').then(({ data }) => {
        const traits = {
          ...data,
          timezone_str: window.timezone,
          surface_str: 'talk'
        }

        if (this.logrocketAppId) {
          this.initLogRocket()
          this.$LogRocket.identify(String(profile.id), {
            name: data.displayName,
            email: data.email,
            ...traits
          })
        }
      }).catch(() => {
        console.log('Error while retrieving session-replay metadata from server')
      })
    },

    // Init LogRocket once, lazily — only after the user is authenticated, so we
    // don't record anonymous landing-page sessions.
    initLogRocket () {
      if (this.logrocketInitialized || !this.logrocketAppId) {
        return
      }

      this.$LogRocket.init(this.logrocketAppId, {
        cookiePolicy: 'crossSite',
        // Same value Sentry uses as its release, so an issue there and the replay
        // here group under one version string.
        release: process.env.VERSION,
        dom: {
          inputSanitizer: false
        },
        network: {
          requestSanitizer,
          responseSanitizer
        }
      })

      // Link the LogRocket session to Sentry so every error captured during this
      // session carries the replay URL as an extra — one-click jump from a Sentry
      // issue to the recording.
      this.$LogRocket.getSessionURL((sessionURL) => {
        this.$Sentry.setExtra('sessionURL', sessionURL)
      })

      this.logrocketInitialized = true
    },

    // identify the user with the session-replay tool (LogRocket) once authenticated
    setSessionReplay () {
      if (this.loading) {
        return
      }

      if (!this.logrocketAppId) {
        return
      }

      if (this.profile && this.authenticated) {
        this.sessionReplayIdentify(this.profile)
      }
    },

    addSentryContext () {
      if (!this.profile) {
        return
      }

      this.$Sentry.setUser({
        id: this.profile.id,
        email: this.profile.email,
        name: this.profile.name,
        company_id: this.profile.company_id,
        company_name: this.profile.company_name
      })
    },

    checkAccesses () {
      if (this.profile?.has_multiple_access) {
        this.getAccesses(true)
      }
    },

    ...mapActions('auth', {
      logoutUser: 'logout',
      getCookieUser: 'getCookieUser',
      getSharedCookie: 'getSharedCookie',
      clearUser: 'clear',
      checkSession: 'check'
    }),

    ...mapActions('cache', [
      'setCurrentCompany'
    ]),

    ...mapActions([
      'resetVuex',
      'setJustLoggedIn',
      'setStatics',
      'setUsage',
      'setWidgetPlatform'
    ]),

    // Applies a season demo in place: the statics carry the season, so the logo and colors follow without a reload
    refetchStatics () {
      return talk2Api.V1.statics.get(this.$store.state.cache.currentCompany?.id).then(res => {
        this.setStatics(res.data)
        storage.local.setItem('statics', JSON.stringify(res.data))
      }).catch(err => {
        console.log(err)
      })
    }
  }
}
</script>
